import { UnfurlItem } from '@lib/external/unfurl';
import {
  DuplicateCheckable,
  isMediaDuplicate,
  isMediaDuplicateById,
} from '@lib/utils/chooseMediaUtils';
import { useCallback } from 'react';

export interface UseDuplicateCheckerProps<T extends DuplicateCheckable & { id: string }> {
  data: T[];
  existingUniqueIds: Record<string, string>;
}

export interface UseDuplicateCheckerReturn<T extends DuplicateCheckable> {
  isDuplicate: (item: T) => boolean;
  isDuplicateById: (itemId: string) => boolean;
}

/**
 * Focused hook for duplicate checking functionality
 * Provides only the essential duplicate detection functions to reduce coupling
 * Generic to work with any type that has uniqueId and id properties
 */
export const useDuplicateChecker = <T extends DuplicateCheckable & { id: string } = UnfurlItem>({
  data,
  existingUniqueIds,
}: UseDuplicateCheckerProps<T>): UseDuplicateCheckerReturn<T> => {
  // Memoized function to check if an item is a duplicate
  const isDuplicate = useCallback(
    (item: T) => isMediaDuplicate(item, existingUniqueIds),
    [existingUniqueIds],
  );

  // Memoized function to check if an item ID is a duplicate
  const isDuplicateById = useCallback(
    (itemId: string) => isMediaDuplicateById(itemId, data, existingUniqueIds),
    [data, existingUniqueIds],
  );

  return {
    isDuplicate,
    isDuplicateById,
  };
};
